import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import {
  selectProjectsData,
  selectProjectsLoading,
  selectProjectsError,
  selectProjectsAllListData,
} from "../../Redux/Slices/projectsManagementSlice";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import Loader from "../../Utility/Loader/Loader";
import ProjectList from "./ProjectList";
import AddProject from "./AddProject";
import EditProject from "./EditProject";
import AddButton from "../../Utility/AddButton/AddButton";
import { ROUTES } from "../../constants";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { PROJECTS_LIST_PAGE_SIZE_STORAGE_KEY } from "../../Utility/Pagination/listPaginationConstants";
import { useListPageSize } from "../../hooks/useListPageSize";
import { buildProjectsListQuery } from "../../Redux/Thunks/projectManagementThunkUtils";
import { downloadProjectsExport } from "../../utils/downloadProjectsExport";
import { showError as showExportError } from "../../utils/toast";
import { RiUpload2Line } from "react-icons/ri";

const EMPTY_LISTING_FILTER = { clientName: "", projectName: "", status: "" };

const ProjectsRoot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  const canCreateProject = can(PERMISSION_KEYS.PROJECTS_CREATE);
  const canViewProjectsList = can(PERMISSION_KEYS.PROJECTS_VIEW);
  const canViewProjectIssuesTab = can(PERMISSION_KEYS.WORK_ITEMS_VIEW);
  const canViewProjectBoardsTab = can(PERMISSION_KEYS.KANBAN_VIEW);
  const canViewProjectBacklogTab = can(PERMISSION_KEYS.WORK_ITEMS_VIEW);

  const defaultProjectTab = can(PERMISSION_KEYS.PROJECTS_DASHBOARD_VIEW)
    ? "dashboard"
    : canViewProjectIssuesTab
      ? "issues"
      : canViewProjectBoardsTab
        ? "boards"
        : canViewProjectBacklogTab
          ? "backlog"
          : can(PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE)
            ? "settings"
            : "issues";

  const { pageSize: limit, setPageSize } = useListPageSize(PROJECTS_LIST_PAGE_SIZE_STORAGE_KEY);
  const projectsData = useSelector(selectProjectsData);
  const allProjectsData = useSelector(selectProjectsAllListData);
  const clientsData = useSelector(selectClientsData);
  const loading = useSelector(selectProjectsLoading);
  const error = useSelector(selectProjectsError);

  const [searchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [listingFilter, setListingFilter] = useState({
    clientName: "",
    projectName: "",
    status: "",
  });
  /** Filters the table was last loaded with — `listingFilter` also holds unapplied dropdown edits. */
  const appliedFilterRef = useRef(EMPTY_LISTING_FILTER);
  const [exportLoading, setExportLoading] = useState(false);
  const fetchProjectsWithFilters = useCallback(
    (page = 1, filterOverrides = null) => {
      const f = filterOverrides ?? listingFilter;
      appliedFilterRef.current = f;
      const clientName = Array.isArray(f.clientName) ? f.clientName[0] : f.clientName;
      const projectName = Array.isArray(f.projectName) ? f.projectName[0] : f.projectName;
      dispatch(
        fetchProjectsData({
          page,
          limit,
          clientName: clientName?.trim() || undefined,
          projectName: projectName?.trim() || undefined,
          status: f.status?.trim() && f.status !== "All" ? f.status : undefined,
        })
      );
    },
    [dispatch, limit, listingFilter]
  );

  const isAddRoute = location.pathname === ROUTES.PROJECTS_ADD;
  const isEditRoute = location.pathname.startsWith(`${ROUTES.PROJECTS_EDIT}/`);
  const prevIsEditRouteRef = useRef(false);

  /** Table uses paginated `projects.data`; edit only refetched `getAll` → `allListData`. Refresh list when returning from edit. */
  useEffect(() => {
    if (prevIsEditRouteRef.current && !isEditRoute) {
      fetchProjectsWithFilters(currentPage);
    }
    prevIsEditRouteRef.current = isEditRoute;
  }, [isEditRoute, currentPage, fetchProjectsWithFilters]);

  useEffect(() => {
    dispatch(fetchProjectsData({ page: 1, limit }));
    dispatch(fetchClientsData({ getAll: true }));
    dispatch(fetchProjectsData({ getAll: true }));
  }, [dispatch, limit]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setListingFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    appliedFilterRef.current = EMPTY_LISTING_FILTER;
    setListingFilter({ clientName: "", projectName: "", status: "" });
    setCurrentPage(1);
    dispatch(fetchProjectsData({ page: 1, limit }));
  };

  /** Exports the projects the table currently shows (last applied filters, every page). */
  const handleExportProjects = async () => {
    const f = appliedFilterRef.current;
    const { clientName, projectName, status } = buildProjectsListQuery(
      {
        clientName: Array.isArray(f.clientName) ? f.clientName[0] : f.clientName,
        projectName: Array.isArray(f.projectName) ? f.projectName[0] : f.projectName,
        status: f.status?.trim() && f.status !== "All" ? f.status : undefined,
      },
      false
    );
    setExportLoading(true);
    try {
      await downloadProjectsExport({ clientName, projectName, status }, "csv");
    } catch (err) {
      showExportError(err?.message || "Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchProjectsWithFilters(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchProjectsWithFilters(page);
  };

  const handlePageSizeChange = (newLimit) => {
    setPageSize(newLimit);
    setCurrentPage(1);
    const f = listingFilter;
    appliedFilterRef.current = f;
    const clientName = Array.isArray(f.clientName) ? f.clientName[0] : f.clientName;
    const projectName = Array.isArray(f.projectName) ? f.projectName[0] : f.projectName;
    dispatch(
      fetchProjectsData({
        page: 1,
        limit: newLimit,
        clientName: clientName?.trim() || undefined,
        projectName: projectName?.trim() || undefined,
        status: f.status?.trim() && f.status !== "All" ? f.status : undefined,
      })
    );
  };

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {error}
          </Typography>
        </div>
      </div>
    );
  }

  // Initial load: show loader
  if (!projectsData && loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }
  const createProjectSlug = (projectName) => {
    if (!projectName) return "";
    return projectName
      .trim()
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  };

  return (
    <div>
      {/* Header - Add button only when there is data */}
      {!isAddRoute && !isEditRoute && (
        <div className="w-full mb-4 flex flex-col sm:flex-row items-center justify-between gap-2 py-3">
          <Typography variant="text1" className="text-xl font-semibold">
            Projects
          </Typography>
          <div className="flex items-center gap-2">
            {/* Export projects matching the applied filters — same visibility as the list itself */}
            {canViewProjectsList ? (
              <Button
                variant="neutral"
                size="vsm"
                title="Export"
                data-tooltipp="Export"
                className="btn-reset square-btn"
                disabled={exportLoading}
                onClick={handleExportProjects}
              >
                <RiUpload2Line className="text-lg text-text-secondary" />
              </Button>
            ) : null}
            {canCreateProject ? (
              <AddButton
                onClick={() =>
                  navigate(ROUTES.PROJECTS_ADD, {
                    state: listingFilter.clientName
                      ? { clientName: listingFilter.clientName }
                      : undefined,
                  })
                }
                tooltip="Add Project"
                tooltipPosition="right"
              />
            ) : null}
          </div>
        </div>
      )}

      {/* Content */}
      {isAddRoute ? (
        <AddProject />
      ) : isEditRoute ? (
        <EditProject />
      ) : (
        <ProjectList
          projectsData={
            projectsData ? { ...projectsData, list: projectsData.list ?? [] } : { list: [] }
          }
          allProjectsData={allProjectsData}
          clientsData={clientsData}
          issuesData={null}
          loading={loading}
          searchTerm={searchTerm}
          currentPage={currentPage}
          setCurrentPage={handlePageChange}
          limit={limit}
          onPageSizeChange={handlePageSizeChange}
          listingFilter={listingFilter}
          handleFilterChange={handleFilterChange}
          handleFilterReset={handleFilterReset}
          applyFilters={applyFilters}
          onRefetchProjects={() => fetchProjectsWithFilters(currentPage)}
          onProjectClick={(project) => navigate(`/projects/${project.id}?tab=${defaultProjectTab}`)}
        />
      )}
    </div>
  );
};

export default ProjectsRoot;
